import { gettext, pgettext } from "../utils/legacy";

export type Vector3D = [number, number, number];
export interface Category {
  name: string;
  label: string;
  color: string;
  points: Array<Vector3D>;
  ediCode?: string;
  noselect?: boolean;
}

export interface Design {
  labels: Array<string>;
  categories: Array<Category>;
}

export type DesignType = "usda_texture" | "stiboka_texture" | "peat_simple" | "peat";
export const designs: Record<DesignType, Design> = {
  usda_texture: {
    labels: [
      pgettext("USDA soil texture", "Sand"),
      pgettext("USDA soil texture", "Silt"),
      pgettext("USDA soil texture", "Clay"),
    ],
    categories: [
      {
        name: "sand",
        label: pgettext("USDA soil texture", "Sand"),
        color: "#FFCE9C",
        points: [
          [100, 0, 0],
          [90, 0, 10],
          [90, 10, 0],
        ],
        ediCode: "USDA:S",
      },
      {
        name: "loamy sand",
        label: pgettext("USDA soil texture", "Loamy sand"),
        color: "#FFCECE",
        points: [
          [90, 10, 0],
          [90, 0, 10],
          [85, 0, 15],
          [70, 30, 0],
        ],
        ediCode: "USDA:LS",
      },
      {
        name: "sandy loam",
        label: pgettext("USDA soil texture", "Sandy loam"),
        color: "#FF9CFF",
        points: [
          [70, 30, 0],
          [85, 0, 15],
          [80, 0, 20],
          [53, 32, 20],
          [53, 42, 5],
          [45, 50, 5],
          [50, 50, 0],
        ],
        ediCode: "USDA:SL",
      },
      {
        name: "sandy clay loam",
        label: pgettext("USDA soil texture", "Sandy clay loam"),
        color: "#FF9C9C",
        points: [
          [80, 0, 20],
          [65, 0, 35],
          [45, 20, 35],
          [45, 27, 28],
          [53, 32, 20],
        ],
        ediCode: "USDA:SCL",
      },
      {
        name: "sandy clay",
        label: pgettext("USDA soil texture", "Sandy clay"),
        color: "#FF0000",
        points: [
          [65, 0, 35],
          [45, 20, 35],
          [45, 0, 55],
        ],
        ediCode: "USDA:SC",
      },
      {
        name: "clay",
        label: pgettext("USDA soil texture", "Clay"),
        color: "#FFFF9C",
        points: [
          [45, 0, 55],
          [0, 0, 100],
          [0, 40, 60],
          [20, 40, 40],
          [45, 15, 40],
        ],
        ediCode: "USDA:C",
      },
      {
        name: "clay loam",
        label: pgettext("USDA soil texture", "Clay loam"),
        color: "#CEFF63",
        points: [
          [45, 15, 40],
          [20, 40, 40],
          [20, 52, 28],
          [45, 27, 28],
        ],
        ediCode: "USDA:CL",
      },
      {
        name: "silty clay",
        label: pgettext("USDA soil texture", "Silty clay"),
        color: "#9CFFCE",
        points: [
          [0, 40, 60],
          [0, 60, 40],
          [20, 40, 40],
        ],
        ediCode: "USDA:SiC",
      },
      {
        name: "silty clay loam",
        label: pgettext("USDA soil texture", "Silty clay loam"),
        color: "#63CE9C",
        points: [
          [0, 72, 28],
          [20, 52, 28],
          [20, 40, 40],
          [0, 60, 40],
        ],
        ediCode: "USDA:SiCL",
      },
      {
        name: "silty loam",
        label: pgettext("USDA soil texture", "Silty loam"),
        color: "#9CCE00",
        points: [
          [50, 50, 0],
          [22, 50, 28],
          [0, 72, 28],
          [0, 88, 12],
          [8, 80, 12],
          [20, 80, 0],
        ],
        ediCode: "USDA:SiL",
      },
      {
        name: "silt",
        label: pgettext("USDA soil texture", "Silt"),
        color: "#00FF31",
        points: [
          [0, 100, 0],
          [20, 80, 0],
          [8, 80, 12],
          [0, 88, 12],
        ],
        ediCode: "USDA:Si",
      },
      {
        name: "loam",
        label: pgettext("USDA soil texture", "Loam"),
        color: "#CE9C00",
        points: [
          [45, 27, 28],
          [22, 50, 28],
          [45, 50, 5],
          [53, 42, 5],
          [53, 32, 20],
        ],
        ediCode: "USDA:L",
      },
    ],
  },
  stiboka_texture: {
    labels: [gettext("Clay"), gettext("Silt"), gettext("Sand")],
    categories: [
      {
        name: "sand",
        label: "Zand",
        color: "#FDD17D",
        points: [
          [100, 0, 0],
          [50, 50, 0],
          [50, 42, 8],
          [92, 0, 8],
        ],
        ediCode: "1",
      },
      {
        name: "loam",
        label: "Leem",
        color: "#E50303",
        points: [
          [50, 50, 0],
          [0, 100, 0],
          [0, 92, 8],
          [50, 42, 8],
        ],
        ediCode: "4",
      },
      {
        name: "light sandy clay",
        label: "Lichte zavel",
        color: "#A7F874",
        points: [
          [92, 0, 8],
          [0, 92, 8],
          [0, 83, 17],
          [83, 0, 17],
        ],
        ediCode: "2",
      },
      {
        name: "heavy sandy clay",
        label: "Zware zavel",
        color: "#71A90B",
        points: [
          [83, 0, 17],
          [0, 83, 17],
          [0, 75, 25],
          [75, 0, 25],
        ],
        ediCode: "5",
      },
      {
        name: "light clay",
        label: "Lichte klei",
        color: "#2B7507",
        points: [
          [75, 0, 25],
          [0, 75, 25],
          [0, 65, 35],
          [65, 0, 35],
        ],
        ediCode: "6",
      },
      {
        name: "heavy clay",
        label: "Zware klei",
        color: "#4A8B6E",
        points: [
          [65, 0, 35],
          [0, 65, 35],
          [0, 0, 100],
        ],
        ediCode: "7",
      },
    ],
  },
  peat_simple: {
    labels: [gettext("Organic matter"), gettext("Clay"), gettext("Silt+Sand")],
    categories: [
      {
        name: "(see other)",
        label: "(zie ander)",
        color: "#CCC",
        points: [
          [100, 0, 0],
          [0, 100, 0],
          [0, 70, 30],
          [85, 0, 15],
        ],
        noselect: true,
      },
      {
        name: "peaty sand",
        label: "Venig zand",
        color: "#FEAA04",
        points: [
          [85, 0, 15],
          [75.4, 8, 16.6],
          [47.47, 8, 44.53],
          [65, 0, 35],
        ],
        ediCode: "8",
      },
      {
        name: "peat",
        label: "Veen",
        color: "#D49CBA",
        points: [
          [65, 0, 35],
          [47.47, 8, 44.53],
          [75.4, 8, 16.6],
          [0, 70, 30],
          [0, 0, 100],
        ],
        ediCode: "12",
      },
    ],
  },
  peat: {
    labels: [gettext("Silt+Sand"), gettext("Clay"), gettext("Organic matter")],
    categories: [
      {
        name: "(see other)",
        label: "(zie ander)",
        color: "#CCC",
        points: [
          [100, 0, 0],
          [0, 100, 0],
          [0, 70, 30],
          [85, 0, 15],
        ],
        noselect: true,
      },
      {
        name: "peaty sand",
        label: "Venig zand",
        color: "#ffde9c",
        points: [
          [85, 0, 15],
          [75.4, 8, 16.6],
          [66.23, 8, 25.77],
          [77.5, 0, 22.5],
        ],
        ediCode: "8",
      },
      {
        name: "peaty clay",
        label: "Venige klei",
        color: "#e9cfa2",
        points: [
          [75.4, 8, 16.6],
          [0, 70, 30],
          [0, 55, 45],
          [66.23, 8, 25.77],
        ],
        ediCode: "9",
      },
      {
        name: "sandy peat",
        label: "Zandig veen",
        color: "#aaaa68",
        points: [
          [77.5, 0, 22.5],
          [66.23, 8, 25.77],
          [47.47, 8, 44.53],
          [65, 0, 35],
        ],
        ediCode: "10",
      },
      {
        name: "clayey peat",
        label: "Kleiig veen",
        color: "#d3a0a8",
        points: [
          [66.23, 8, 25.77],
          [0, 55, 45],
          [0, 30, 70],
          [47.47, 8, 44.53],
        ],
        ediCode: "11",
      },
      {
        name: "peat",
        label: "Veen",
        color: "#be71ae",
        points: [
          [65, 0, 35],
          [0, 30, 70],
          [0, 0, 100],
        ],
        ediCode: "12",
      },
    ],
  },
};
